import { Socket } from 'socket.io-client';
import toast from 'react-hot-toast';

// Types for interceptor functions
type SocketRequestInterceptor = (config: SocketConnectionConfig) => SocketConnectionConfig | Promise<SocketConnectionConfig>;
type SocketResponseInterceptor = {
  onConnect?: (socket: Socket) => void;
  onDisconnect?: (reason: string, socket: Socket) => void;
  onError?: (error: Error, socket: Socket) => void;
  onEvent?: (event: string, data: any, socket: Socket) => any;
};

interface SocketConnectionConfig {
  auth?: Record<string, any>;
  query?: Record<string, any>;
  headers?: Record<string, string>;
  [key: string]: any;
}

class SocketInterceptor {
  private requestInterceptors: SocketRequestInterceptor[] = [];
  private responseInterceptors: SocketResponseInterceptor[] = [];

  // Add request interceptor
  useRequestInterceptor(interceptor: SocketRequestInterceptor) {
    this.requestInterceptors.push(interceptor);
    return () => {
      const index = this.requestInterceptors.indexOf(interceptor);
      if (index > -1) {
        this.requestInterceptors.splice(index, 1);
      }
    };
  }

  // Add response interceptor
  useResponseInterceptor(interceptor: SocketResponseInterceptor) {
    this.responseInterceptors.push(interceptor);
    return () => {
      const index = this.responseInterceptors.indexOf(interceptor);
      if (index > -1) {
        this.responseInterceptors.splice(index, 1);
      }
    };
  }

  // Apply request interceptors
  async applyRequestInterceptors(config: SocketConnectionConfig): Promise<SocketConnectionConfig> {
    let modifiedConfig = { ...config };
    
    for (const interceptor of this.requestInterceptors) {
      modifiedConfig = await interceptor(modifiedConfig);
    }
    
    return modifiedConfig;
  }

  // Apply response interceptors for connect event
  applyConnectInterceptor(socket: Socket) {
    this.responseInterceptors.forEach(interceptor => {
      if (interceptor.onConnect) {
        interceptor.onConnect(socket);
      }
    });
  }

  // Apply response interceptors for disconnect event
  applyDisconnectInterceptor(reason: string, socket: Socket) {
    this.responseInterceptors.forEach(interceptor => {
      if (interceptor.onDisconnect) {
        interceptor.onDisconnect(reason, socket);
      }
    });
  }

  // Apply response interceptors for error event
  applyErrorInterceptor(error: Error, socket: Socket) {
    this.responseInterceptors.forEach(interceptor => {
      if (interceptor.onError) {
        interceptor.onError(error, socket);
      }
    });
  }

  // Apply response interceptors for custom events
  applyEventInterceptor(event: string, data: any, socket: Socket): any {
    let modifiedData = data;
    
    this.responseInterceptors.forEach(interceptor => {
      if (interceptor.onEvent) {
        const result = interceptor.onEvent(event, data, socket);
        if (result !== undefined) {
          modifiedData = result;
        }
      }
    });
    
    return modifiedData;
  }
}

// Create singleton instance
const socketInterceptor = new SocketInterceptor();

// Default request interceptor: Add authentication token and userId
socketInterceptor.useRequestInterceptor((config) => {
  const token = localStorage.getItem('token');
  
  // Try to get userId from localStorage (if stored separately)
  // Otherwise, it will be added via AuthContext in components
  let userId = localStorage.getItem('userId');
  
  // If userId not in localStorage, try to get from token payload (if JWT)
  if (!userId && token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      userId = payload.userId || payload.id || payload.sub;
    } catch (e) {
      // Token is not JWT or invalid, ignore
    }
  }
  
  // Add token to auth if available
  if (token) {
    config.auth = {
      ...config.auth,
      token,
    };
  }

  // Add userId to auth if available
  if (userId) {
    config.auth = {
      ...config.auth,
      userId,
    };
  }

  return config;
});

// Default response interceptor: Handle errors globally
socketInterceptor.useResponseInterceptor({
  onError: (error: Error, socket: Socket) => {
    console.error('Socket error intercepted:', error);
    
    // Handle authentication errors
    if (error.message?.includes('unauthorized') || error.message?.includes('authentication')) {
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        toast.error('Session expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
    } else {
      // Show generic error toast for other errors
      toast.error(error.message || 'Socket connection error');
    }
  },

  onDisconnect: (reason: string, socket: Socket) => {
    // Handle server-initiated disconnects (like unauthorized)
    if (reason === 'io server disconnect' || reason === 'unauthorized') {
      console.warn('Socket disconnected by server:', reason);
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        toast.error('Connection terminated. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
    } else {
      console.log('Socket disconnected:', reason);
    }
  },

  onConnect: (socket: Socket) => {
    console.log('✅ Socket connected with interceptor:', socket.id);
  },
});

export default socketInterceptor;

